import { useEffect, useRef, useState } from "react";

const remainingSeconds = (target: Date | null): number =>
  target ? Math.max(0, Math.ceil((target.getTime() - Date.now()) / 1000)) : 0;

/**
 * Counts the whole seconds remaining until `target`, ticking once per second.
 * Returns 0 once the target passes (or when `target` is null). Self-cleans its
 * interval and re-syncs whenever `target` changes.
 *
 * The remaining value is corrected synchronously on the render where `target`
 * changes (not one render later via an effect), so consumers that branch on it
 * — disabling a button, mounting a banner, clearing state at zero — never
 * observe a stale `0` frame.
 *
 * Pass a stable `Date` (e.g. one held in state) so the effect does not re-run
 * on every render.
 */
export function useCountdown(target: Date | null): number {
  const [secondsLeft, setSecondsLeft] = useState<number>(() => remainingSeconds(target));

  // Resync during render when the target changes. React discards this render and
  // re-renders with the corrected value before committing, so the value the hook
  // returns is correct on the same render the target changed.
  const trackedTarget = useRef(target);
  if (trackedTarget.current !== target) {
    trackedTarget.current = target;
    setSecondsLeft(remainingSeconds(target));
  }

  useEffect(() => {
    if (!target) {
      return;
    }
    const id = setInterval(() => {
      const left = remainingSeconds(target);
      setSecondsLeft(left);
      if (left <= 0) {
        clearInterval(id);
      }
    }, 1000);

    return () => clearInterval(id);
  }, [target]);

  return secondsLeft;
}
